/**
 * EPL Admin - Asignación de Sistemas EPL
 * Vista EXCLUSIVA para asignar laboratorios EPL a usuarios existentes
 * 
 * NOTA: La gestión general de usuarios (crear, editar, eliminar, reset password)
 * se realiza desde /admin/config
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  BarChart3,
  Settings as SettingsIcon,
  Loader,
  Check,
  Square,
  ArrowUpDown,
  ExternalLink,
  XCircle,
  CheckCircle
} from 'lucide-react';
import { useEplAdminUsers } from '../../hooks/useEplAdmin';
import { useNavigate } from 'react-router-dom';

const AdminUsers = () => {
  const navigate = useNavigate();
  const { users, loading, error, filters, setFilters, actions } = useEplAdminUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [sortBy, setSortBy] = useState('fechaRegistro');
  const [sortOrder, setSortOrder] = useState('desc');
  const searchTimeoutRef = useRef(null);

  // Debounce para búsqueda: espera 500ms después de que el usuario deja de escribir
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters({ ...filters, search: searchTerm });
    }, 500); // 500ms de debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Ya no se llama setFilters aquí, lo hace el useEffect con debounce
  };

  const handleRoleChange = (role) => {
    setFilters({ ...filters, role });
  };

  const handleStatusChange = (status) => {
    setFilters({ ...filters, status });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Si ya estamos ordenando por este campo, cambiar dirección
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Nuevo campo, ordenar descendente por defecto
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredUsers = users
    .filter(user => {
      const matchesSearch = user.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'fechaRegistro') {
        aValue = new Date(a.fechaRegistro || 0);
        bValue = new Date(b.fechaRegistro || 0);
      } else if (sortBy === 'nombre') {
        aValue = a.nombre?.toLowerCase() || '';
        bValue = b.nombre?.toLowerCase() || '';
      } else if (sortBy === 'email') {
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
      } else if (sortBy === 'role') {
        aValue = a.role || '';
        bValue = b.role || '';
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const handleAssignSystems = (user) => {
    setSelectedUser(user);
    setShowAssignModal(true);
  };

  const handleViewStats = async (user) => {
    setSelectedUser(user);
    setShowStatsModal(true);
    
    // Cargar estadísticas solo cuando el admin las solicita
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No hay token de autenticación');
        alert('Sesión expirada. Por favor, inicia sesión nuevamente.');
        return;
      }
      
      const response = await fetch(`http://localhost:3002/api/epl/admin/users/${user.id}/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error en respuesta:', errorData);
        
        if (response.status === 401) {
          alert('Sesión expirada. Por favor, inicia sesión nuevamente.');
          window.location.href = '/login';
          return;
        }
        
        throw new Error(errorData.error?.message || 'Error al cargar estadísticas');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Actualizar el usuario con las estadísticas reales
        setSelectedUser({
          ...user,
          completedLabs: data.data.completedLabs,
          avgScore: data.data.avgScore,
          lastActive: data.data.lastActive
        });
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      alert('Error al cargar estadísticas: ' + error.message);
    }
  };

  const handleSaveSystemAssignment = async () => {
    if (!selectedUser) return;
    
    setSavingAssignment(true);
    
    try {
      // Obtener checkboxes marcados
      const checkboxes = document.querySelectorAll('#system-assignment-modal input[type="checkbox"]');
      const selectedSystems = Array.from(checkboxes)
        .filter(cb => cb.checked)
        .map(cb => cb.value);
      
      await actions.updateSystems(selectedUser.id, selectedSystems);
      
      setShowAssignModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error guardando asignación:', error);
      alert(`Error al guardar: ${error.message}`);
    } finally {
      setSavingAssignment(false);
    }
  };

  const getRoleBadge = (rol) => {
    const badges = {
      admin: 'bg-purple-100 text-purple-700',
      docente: 'bg-blue-100 text-blue-700',
      estudiante: 'bg-green-100 text-green-700'
    };
    return badges[rol] || 'bg-gray-100 text-gray-600';
  };

  const getStatusBadge = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-700' 
      : 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Asignación de Sistemas EPL</h2>
            <p className="text-gray-600">Asignar laboratorios EPL a usuarios del sistema</p>
          </div>
          <button 
            onClick={() => navigate('/admin/config')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            <span>Gestión de Usuarios</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={filters.role}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todos los roles</option>
            <option value="admin">Admin</option>
            <option value="docente">Docente</option>
            <option value="estudiante">Estudiante</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <p className="font-semibold">Error al cargar usuarios:</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Vista Desktop - Tabla tradicional */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('fechaRegistro')}
                  title="Ordenar por fecha de registro"
                >
                  <div className="flex items-center gap-1">
                    Fecha Registro
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('nombre')}
                  title="Ordenar por nombre"
                >
                  <div className="flex items-center gap-1">
                    Usuario
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sistemas Asignados
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estadísticas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.fechaRegistro ? new Date(user.fechaRegistro).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {user.fechaRegistro ? new Date(user.fechaRegistro).toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                        {user.nombre.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.nombre}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.role ? (
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    ) : (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">
                        Sin rol
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(user.eplSystemsAssigned || []).length > 0 ? (
                        user.eplSystemsAssigned.map((system, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {system}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">Sin asignación</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      {/* Labs completados */}
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-900">{user.completedLabs || 0}</span>
                        <span className="text-xs text-gray-500">labs</span>
                      </div>
                      
                      {/* Separador */}
                      <span className="text-gray-300">|</span>
                      
                      {/* Promedio */}
                      <div className="flex items-center gap-1">
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                        <span className={`text-sm font-medium ${
                          user.avgScore >= 70 ? 'text-green-600' : 
                          user.avgScore >= 50 ? 'text-yellow-600' : 
                          user.avgScore > 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {user.avgScore || 0}%
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(user.status)}`}>
                      {user.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleAssignSystems(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Asignar sistemas EPL"
                      >
                        <SettingsIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleViewStats(user)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Ver estadísticas"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Vista Móvil - Cards con acciones debajo */}
        <div className="md:hidden divide-y divide-gray-200">
          {filteredUsers.map((user) => (
            <div key={user.id} className="p-4 hover:bg-gray-50">
              {/* Usuario Info */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                  {user.nombre.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{user.nombre}</div>
                  <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {user.fechaRegistro ? new Date(user.fechaRegistro).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Fecha no disponible'}
                  </div>
                  
                  {/* Rol y Estado */}
                  <div className="flex items-center gap-2 mt-1.5">
                    {user.role ? (
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                        Sin rol
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(user.status)}`}>
                      {user.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sistemas Asignados */}
              {(user.eplSystemsAssigned || []).length > 0 && (
                <div className="mb-2">
                  <div className="flex flex-wrap gap-1">
                    {user.eplSystemsAssigned.map((system, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {system}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Estadísticas */}
              <div className="flex gap-4 text-xs text-gray-600 mb-3">
                <span>{user.completedLabs || 0} labs completados</span>
                <span>Promedio: {user.avgScore || 0}%</span>
              </div>

              {/* Acciones - Solo asignación y estadísticas */}
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleAssignSystems(user)}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Asignar sistemas EPL"
                >
                  <SettingsIcon className="w-5 h-5" />
                  <span className="text-xs font-medium">Sistemas</span>
                </button>
                <button
                  onClick={() => handleViewStats(user)}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                  title="Ver estadísticas"
                >
                  <BarChart3 className="w-5 h-5" />
                  <span className="text-xs font-medium">Estadísticas</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Assign Systems Modal */}
      {showAssignModal && selectedUser && (
        <AssignSystemsModal
          user={selectedUser}
          onClose={() => setShowAssignModal(false)}
          onSave={async (systems) => {
            setSavingAssignment(true);
            try {
              await actions.updateSystems(selectedUser.id, systems);
              
              // Actualizar usuario seleccionado para reflejar cambios inmediatamente
              setSelectedUser(prev => ({
                ...prev,
                eplSystemsAssigned: systems
              }));
              
              setShowAssignModal(false);
              
              // Mostrar confirmación visual
              const toast = document.createElement('div');
              toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
              toast.textContent = '✓ Sistemas actualizados correctamente';
              document.body.appendChild(toast);
              setTimeout(() => {
                toast.classList.add('animate-fade-out');
                setTimeout(() => toast.remove(), 300);
              }, 2000);
            } catch (err) {
              alert(`Error al guardar: ${err.message}`);
            } finally {
              setSavingAssignment(false);
            }
          }}
          saving={savingAssignment}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSave={async (updatedData) => {
            try {
              await actions.updateUser(selectedUser.id, updatedData);
              setShowEditModal(false);
              setSelectedUser(null);
            } catch (error) {
              console.error('Error actualizando usuario:', error);
              alert(`Error al actualizar: ${error.message}`);
            }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <DeleteConfirmModal
          user={selectedUser}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      {/* User Statistics Modal */}
      {showStatsModal && selectedUser && (
        <UserStatsModal
          user={selectedUser}
          onClose={() => {
            setShowStatsModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Nota informativa */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Nota:</strong> Para crear, editar o eliminar usuarios, visita la{' '}
          <button 
            onClick={() => navigate('/admin/config')}
            className="underline font-semibold hover:text-blue-900"
          >
            Configuración de Administrador
          </button>
        </p>
      </div>

      {/* Summary */}
      <div className="mt-4 text-sm text-gray-600">
        Mostrando {filteredUsers.length} de {users.length} usuarios
      </div>
    </div>
  );
};

// Component for user statistics modal
const UserStatsModal = ({ user, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Estadísticas del Usuario
              </h3>
              <p className="text-sm text-gray-600">
                {user.nombre}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XCircle className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* User Info Card */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Email</p>
              <p className="text-sm font-medium text-gray-900">{user.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Rol</p>
              <p className="text-sm font-medium text-gray-900">
                {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Estado</p>
              <p className="text-sm font-medium text-gray-900">
                {user.status === 'active' ? 'Activo' : 'Inactivo'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Labs Completados</p>
              <p className="text-sm font-medium text-gray-900">{user.completedLabs || 0}</p>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="space-y-4 mb-6">
          <h4 className="font-semibold text-gray-900">Rendimiento General</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-blue-700">Labs Completados</span>
                <CheckCircle className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-900">{user.completedLabs || 0}</p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-green-700">Promedio</span>
                <BarChart3 className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-900">{user.avgScore || 0}%</p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-purple-700">Sistemas Asignados</span>
                <SettingsIcon className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-900">
                {user.eplSystemsAssigned?.length || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Assigned Systems */}
        <div className="space-y-3 mb-6">
          <h4 className="font-semibold text-gray-900">Sistemas EPL Asignados</h4>
          {user.eplSystemsAssigned && user.eplSystemsAssigned.length > 0 ? (
            <div className="space-y-2">
              {user.eplSystemsAssigned.map((system, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-900">{system}</span>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">No hay sistemas asignados</p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {user.avgScore > 0 && (
          <div className="space-y-2 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">Progreso General</span>
              <span className="font-semibold text-gray-900">{user.avgScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${user.avgScore}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for system assignment modal
const AssignSystemsModal = ({ user, onClose, onSave, saving }) => {
  // Convertir sistemas legacy a systemCards individuales
  const convertLegacyToSystemCards = (systems) => {
    if (!systems || !Array.isArray(systems)) return [];
    
    const legacyMapping = {
      'EPL-Leader Lab': ['gestion_crisis', 'dinamicas_equipo', 'comunicacion_estrategica'],
      'EPL-Innova System': ['innovafast', 'innovatech', 'transforma360'],
      'EPL-Decision System': ['dss_builder', 'analisis_riesgo', 'planificacion_estrategica']
    };
    
    const converted = [];
    systems.forEach(sys => {
      if (legacyMapping[sys]) {
        // Es un sistema legacy, expandir a systemCards individuales
        converted.push(...legacyMapping[sys]);
      } else {
        // Ya es un systemCard individual, mantener
        converted.push(sys);
      }
    });
    
    return [...new Set(converted)]; // Eliminar duplicados
  };
  
  const [selectedSystems, setSelectedSystems] = useState(
    convertLegacyToSystemCards(user.eplSystemsAssigned || [])
  );

  const toggleSystem = (system) => {
    setSelectedSystems(prev => 
      prev.includes(system)
        ? prev.filter(s => s !== system)
        : [...prev, system]
    );
  };

  const handleSave = () => {
    onSave(selectedSystems);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Asignar Sistemas EPL
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Usuario: {user.nombre}
        </p>
        
        <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto">
          {/* EPL-Leader Lab */}
          <div>
            <div className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">
              EPL-Leader Lab
            </div>
            <div className="space-y-1.5 pl-2">
              <div 
                onClick={() => toggleSystem('gestion_crisis')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('gestion_crisis') 
                    ? 'bg-purple-600 border-purple-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('gestion_crisis') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">Gestión de Crisis</span>
              </div>
              <div 
                onClick={() => toggleSystem('dinamicas_equipo')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('dinamicas_equipo') 
                    ? 'bg-purple-600 border-purple-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('dinamicas_equipo') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">Dinámicas de Equipo</span>
              </div>
              <div 
                onClick={() => toggleSystem('comunicacion_estrategica')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('comunicacion_estrategica') 
                    ? 'bg-purple-600 border-purple-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('comunicacion_estrategica') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">Comunicación Estratégica</span>
              </div>
            </div>
          </div>

          {/* EPL-Innova System */}
          <div>
            <div className="text-xs font-semibold text-green-700 mb-2 uppercase tracking-wide">
              EPL-Innova System
            </div>
            <div className="space-y-1.5 pl-2">
              <div 
                onClick={() => toggleSystem('innovafast')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-green-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('innovafast') 
                    ? 'bg-green-600 border-green-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('innovafast') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">InnovaFast</span>
              </div>
              <div 
                onClick={() => toggleSystem('innovatech')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-green-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('innovatech') 
                    ? 'bg-green-600 border-green-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('innovatech') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">InnovaTech</span>
              </div>
              <div 
                onClick={() => toggleSystem('transforma360')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-green-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('transforma360') 
                    ? 'bg-green-600 border-green-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('transforma360') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">Transforma360</span>
              </div>
            </div>
          </div>

          {/* EPL-Decision System */}
          <div>
            <div className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">
              EPL-Decision System
            </div>
            <div className="space-y-1.5 pl-2">
              <div 
                onClick={() => toggleSystem('dss_builder')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('dss_builder') 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('dss_builder') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">DSS Builder</span>
              </div>
              <div 
                onClick={() => toggleSystem('analisis_riesgo')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('analisis_riesgo') 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('analisis_riesgo') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">Análisis de Riesgo</span>
              </div>
              <div 
                onClick={() => toggleSystem('planificacion_estrategica')}
                className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                  selectedSystems.includes('planificacion_estrategica') 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'bg-white border-gray-300'
                }`}>
                  {selectedSystems.includes('planificacion_estrategica') && (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">Planificación Estratégica</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
